import { useEffect } from 'react';
import Widget from 'components/Dashboard/Widget';
import { useKPIGetCustomerSatisfactionByCaseType } from 'api/kpi/kpi';
import { KPIsWidgetEnum, LongTitles } from '../../utils';
import useManagementKpiFilter from './useManagementKpiFilter';
import {
  ControlFigures,
  nf,
  tableStyle,
  tdNumStyle,
  tdStyle,
  thNumStyle,
  thStyle,
  WidgetState,
} from './shared';

/**
 * P5 — kundnöjdhet uppdelat på hemleverans och inköp.
 *
 * Antal svar står alltid bredvid snittet. Svarsfrekvensen ligger runt 40 %, så utan den
 * siffran blir en vecka med tolv svar en "trend".
 */
const CustomerSatisfactionWidget = () => {
  const { isReady, isDateRangeTooLong, filter, deps } = useManagementKpiFilter();
  const getData = useKPIGetCustomerSatisfactionByCaseType();

  useEffect(() => {
    if (isReady) {
      getData.mutate({ data: filter });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const result = getData.data;

  // Perioden kan bestå av flera grupper — väg ihop snittet på antal svar, inte på antal rader.
  const byCategory = new Map<
    string,
    { responses: number; ratingSum: number; sent: number }
  >();
  (result?.dataPoints ?? []).forEach((dp) => {
    const current = byCategory.get(dp.categoryName) ?? {
      responses: 0,
      ratingSum: 0,
      sent: 0,
    };
    current.responses += dp.responses;
    current.ratingSum += (dp.averageRating ?? 0) * dp.responses;
    current.sent += dp.surveysSent;
    byCategory.set(dp.categoryName, current);
  });

  const rows = Array.from(byCategory.entries())
    .map(([name, v]) => ({
      name,
      responses: v.responses,
      sent: v.sent,
      average: v.responses > 0 ? v.ratingSum / v.responses : null,
      responseRate: v.sent > 0 ? (v.responses / v.sent) * 100 : null,
    }))
    .sort((a, b) => b.responses - a.responses);

  return (
    <Widget
      title={LongTitles[KPIsWidgetEnum.CustomerSatisfaction]}
      count={result?.totalResponses ?? 0}
      isLoading={getData.isLoading}
      info="Snittbetyg 1–5 från Zäkras kundenkät, redovisat bara på inköp och hemleverans (auktionsbilar ingår i inköp). Perioden räknas på när kunden svarade. Svarsfrekvensen jämför mot utskicken i samma period och är en indikation, inte en kohortsiffra."
    >
      <WidgetState
        isLoading={getData.isLoading}
        isDateRangeTooLong={isDateRangeTooLong}
        isEmpty={rows.length === 0}
      >
        {rows.map((row) => (
          <div key={row.name} style={{ marginBottom: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
              <span style={{ flex: 1 }}>{row.name}</span>
              <strong style={{ fontSize: '24px' }}>
                {row.average === null ? '–' : nf(row.average, 2)}
              </strong>
              <span style={{ fontSize: '12px', color: '#666' }}>av 5</span>
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {nf(row.responses)} svar av {nf(row.sent)} utskick
              {row.responseRate !== null ? ` (${nf(row.responseRate, 1)} %)` : ''}
            </div>
          </div>
        ))}

        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Ärendetyp</th>
              <th style={thNumStyle}>Snittbetyg</th>
              <th style={thNumStyle}>Svar</th>
              <th style={thNumStyle}>Utskick</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.name} style={{ borderBottom: '1px solid #eee' }}>
                <td style={tdStyle}>{row.name}</td>
                <td style={tdNumStyle}>
                  {row.average === null ? '–' : nf(row.average, 2)}
                </td>
                <td style={tdNumStyle}>{nf(row.responses)}</td>
                <td style={tdNumStyle}>{nf(row.sent)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <ControlFigures
          items={[
            result?.averageRating !== undefined && result?.averageRating !== null
              ? `Totalt snitt ${nf(result.averageRating, 2)} på ${nf(result.totalResponses)} svar`
              : null,
          ]}
        />
      </WidgetState>
    </Widget>
  );
};

export default CustomerSatisfactionWidget;
